import { Link, useMatchRoute } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";

import {
  Badge,
  EmptyState,
  ErrorState as DesignErrorState,
  GoInstead as DesignGoInstead,
  SignedOut as DesignSignedOut,
  LoadingRows,
  cn,
} from "@magicweave/design";
import { isApiError } from "@magicweave/api";

import { useSession } from "../auth/context";
import { Logo } from "./logo";
import { neverAsked } from "./query-state";
import { environmentTone } from "./shell.model";

/**
 * The frame every screen renders inside, plus the three states every screen
 * has: loading, failed, empty.
 *
 * The drawings moved to `@magicweave/design`, where the staff shell's copies of
 * them went too — a console is judged on its bad paths, and two shells each
 * inventing an error card is two chances to forget the request id, which is the
 * entire reason a support ticket is actionable. What stays here is the plumbing
 * the package must not grow: the router, the session, and the shape of this
 * codebase's errors.
 *
 * Every screen's import is unchanged. The names below are the same names.
 */
export {
  PageHeader,
  RowList,
  Row,
  RowGroup,
  NewSince,
  RowHorizon,
  EmptyState,
  LoadingRows,
  NotBuiltYet,
} from "@magicweave/design";

/**
 * A failure the user can act on.
 *
 * An adapter, and the reason there is one: `@magicweave/design` draws surfaces
 * and knows nothing about our generated API client, so the shape of an
 * `ApiError` is read here and handed over as plain props. The alternative is a
 * design system that imports the OpenAPI client, which would make every
 * screen's styling depend on a contract.
 *
 * `isRetryable` is decided here for the same reason — it is a property of the
 * status code, which is a fact about the wire rather than about the drawing.
 */
export function ErrorState({
  error,
  onRetry,
  title,
}: {
  error: unknown;
  onRetry?: () => void;
  /**
   * What could not be done, when this screen knows better than the wire does.
   *
   * The server's sentence says the service was unavailable; it cannot say
   * which read that was or what the reader was looking at. A caller that knows
   * — "the environments this screen chooses from could not be read" — puts
   * that here and the wire's own words stay underneath it.
   */
  title?: ReactNode;
}) {
  const api = isApiError(error) ? error : null;
  const message =
    api?.message ?? (error instanceof Error ? error.message : "Something went wrong.");

  return (
    <DesignErrorState
      title={title}
      message={message}
      hint={api?.hint ?? undefined}
      // A validation refusal carries its reasons, and they are the whole point
      // of it — a publish that says only "there is one thing to fix" and prints
      // a request id is a refusal nobody can act on, which is the support
      // ticket decision 0012 says we created ourselves. All of them, not the
      // first: somebody fixing four problems one publish at a time stops
      // trusting the check.
      problems={api?.problems}
      requestId={api?.requestId ?? undefined}
      onRetry={onRetry && (!api || api.isRetryable) ? onRetry : undefined}
    />
  );
}

/**
 * One place to go instead, with the reason it is the right one.
 *
 * The link is built here because `Link` typechecks a path against this app's
 * own route tree, which is not something a shared package can have.
 */
export function GoInstead({
  to,
  params,
  label,
  children,
}: {
  to: string;
  params?: object;
  label: string;
  children: ReactNode;
}) {
  return (
    <DesignGoInstead
      link={
        <Link to={to} params={params as never}>
          {label}
        </Link>
      }
    >
      {children}
    </DesignGoInstead>
  );
}

/**
 * Renders exactly one of failed, never-asked, loading, empty or content.
 *
 * **Failed comes first, and that is the ordering this once got wrong.** A
 * refused read is not a kind of emptiness — HOUSE-RULES §5 — so `query.error`
 * is tested before the branch that decides a query was never asked. With the
 * two the other way round, a screen whose reads the platform had just refused
 * drew the `blocked` state below and named a cause nobody had checked.
 *
 * Stays here: it is this console's query plumbing rather than a drawing. The
 * ordering matters and is easy to get wrong by hand — an `isLoading` check that
 * runs after an `isEmpty` check flashes "no players" for a moment on every
 * navigation, which reads as data loss.
 *
 * **The trap, and the reason there are five branches rather than four.**
 * TanStack Query v5 has two statuses, not one. `status` answers *do I have
 * data*, and `fetchStatus` answers *is a request in flight*. A query with
 * `enabled: false` has never had data and never will until something enables
 * it, so it reports `isPending: true` **forever**, with `fetchStatus: "idle"`.
 * It is not loading; it is never going to load.
 *
 * Read `isPending` alone and every environment-scoped screen reached from a
 * project with no environment draws `LoadingRows` for as long as it is open —
 * a skeleton that resolves into nothing, on a screen where the honest answer is
 * one sentence. Confirmed on Stats.
 *
 * So a query that is pending *and* idle *and* has no data was never asked, and
 * that is a real emptiness rather than a wait: there is no environment (or
 * player, or release, or pack) to ask about. It renders the `blocked` empty
 * state — *do something else first* — because the reader cannot act on this
 * screen and a centred invitation here would tell them to fill a list that has
 * nothing to hold it.
 *
 * **Why it waits first.** "Disabled" has two meanings and they look identical:
 * *there is no environment* and *the environments read has not come back yet*.
 * Every guarded id on these screens is read out of another query — Stats takes
 * its environment from the provider's `useEnvironments`, Ad networks picks one
 * out of the same list — so on a cold load every one of them is briefly
 * disabled on the way to being fine. Committing to the sentence on the first
 * render would put "there is nothing here to ask about" on screen for a moment
 * on a project with four environments, which is the flash the ordering above
 * exists to prevent, wearing different words.
 *
 * The two are told apart by time, because nothing else tells them apart: *not
 * yet* resolves within one round trip and *not ever* does not. So a disabled
 * query keeps the skeleton for `SETTLE_MS` and only then becomes the sentence.
 * On the common path the grace never elapses and nothing flashes; on the stuck
 * path the skeleton stops being a promise the screen cannot keep.
 *
 * This is decided once, here. A screen that guards its own query with
 * `enabled:` gets the truthful render for free, and a screen that checks
 * `isPending` by hand calls `neverAsked` rather than reimplementing the
 * two-status test: every copy of it is a copy that will be written wrong.
 */
export function Async<T>({
  query,
  upstream,
  empty,
  children,
}: {
  query: {
    data: T | undefined;
    isPending: boolean;
    /**
     * TanStack's second status. Optional, because a few callers hand over a
     * read they composed themselves out of two queries rather than a query
     * result — see `releases/history.tsx`. Absent means "this cannot be
     * disabled", which is true of a composed read: it is pending only while one
     * of its parts genuinely is.
     */
    fetchStatus?: "fetching" | "paused" | "idle";
    error: unknown;
    refetch: () => void;
  };
  /**
   * The read this one is waiting on, when the caller has it in hand.
   *
   * A disabled query cannot see why it was disabled — that is the whole of
   * `neverAsked`, and the comment on `NothingToAskAbout` says so. The caller
   * can: a screen whose id comes out of `useEnvironments` knows whether that
   * read answered, was refused, or has not come back yet. Handing the query
   * over is how that reaches the reader, and it is why this takes a query
   * rather than a sentence — the refusal, its hint and its request id are all
   * on it, and **Try again** here retries the read that actually failed.
   *
   * Without it a refused read upstream lands in the `blocked` state below,
   * which tells a studio with three environments that it has none.
   */
  upstream?: { error: unknown; refetch?: () => void };
  empty?: (data: T) => ReactNode;
  children: (data: T) => ReactNode;
}) {
  // Before every early return, because a hook that runs on one render and not
  // the next is a hook-order violation — and an error arriving mid-life is
  // exactly that sequence.
  const settledAsNever = useSettledAsNever(neverAsked(query));

  // An error is a fourth thing, not an empty state (HOUSE-RULES §5), and it
  // outranks every emptiness under it. It used to sit third, under a branch
  // that reads "this query is disabled" — so a screen whose read was refused
  // drew a `blocked` state naming a cause it had not checked.
  if (query.error) return <ErrorState error={query.error} onRetry={query.refetch} />;
  // Asked for nothing because the read that would have said what to ask about
  // was refused. That is not a project with nothing in it, and it is the one
  // case where the reason a query was never asked is known here.
  if (upstream?.error && neverAsked(query))
    return (
      <ErrorState
        title="This part of the screen could not be read"
        error={upstream.error}
        onRetry={upstream.refetch}
      />
    );
  if (settledAsNever) return <NothingToAskAbout />;
  if (query.isPending) return <LoadingRows />;
  if (query.data === undefined) return <ErrorState error={new Error("No data returned.")} />;
  const isEmpty = empty?.(query.data);
  if (isEmpty) return <>{isEmpty}</>;
  return <>{children(query.data)}</>;
}

/**
 * One round trip, near enough. Long enough that the read a disabled query is
 * waiting on has started, short enough that nobody reads a skeleton twice.
 */
const SETTLE_MS = 600;

/**
 * Whether a disabled query has stayed disabled long enough to be believed.
 *
 * False on the first render and for `SETTLE_MS` after, so the ordinary
 * cold-load sequence — id unknown, id known, data — never shows the sentence.
 * It resets the moment the query stops being disabled, so a screen that clears
 * its selection and chooses again waits afresh rather than remembering that it
 * once gave up.
 *
 * Note for tests: this needs a real render with effects. `renderToStaticMarkup`
 * never runs one, so a static render of a disabled query shows `LoadingRows` —
 * that is the harness, not the screen. The decision itself is `neverAsked`,
 * which is pure and tested in `query-state.test.ts`.
 */
function useSettledAsNever(disabled: boolean): boolean {
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (!disabled) {
      setSettled(false);
      return;
    }
    const timer = setTimeout(() => setSettled(true), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [disabled]);
  return disabled && settled;
}

/**
 * What a screen says when the thing it is about has not been chosen.
 *
 * `blocked` rather than `nothing-yet`: the reader cannot do anything here, and
 * the centred invitation with a button is for a screen where they can. It
 * cannot name the prerequisite — `Async` does not know which guard was false,
 * and the guards are an environment, an org, a player, a release, a pack, a
 * room — so it says what is true of all of them and leaves the way on to the
 * screen around it. A screen that can name its own prerequisite should say so
 * above this rather than teach `Async` about it.
 *
 * And it must not reach for a cause it has not checked. A screen that knows
 * its own upstream read was refused hands the query over as `upstream`, and
 * the reader gets the refusal with a **Try again** instead of this.
 */
function NothingToAskAbout() {
  return (
    <EmptyState
      kind="blocked"
      title="There is nothing here to ask about yet"
      // Two claims have come off this sentence, both for the same reason: it
      // sees one disabled query and cannot see why. "Nothing has gone wrong"
      // went first. "Which a project has none of until one is created" went
      // with it — a studio with three environments, on a screen whose reads
      // were refused, was being told it had none. What is left is what this
      // branch can actually see, plus where the choice is made.
      description="This part of the screen reads from something that has not been chosen — usually an environment, which the switcher at the top of the page chooses. Nothing is loading."
    />
  );
}

export function EnvironmentBadge({ kind }: { kind: string }) {
  return (
    <Badge tone={environmentTone(kind)}>
      {/* A dot as well as the word: severity is never carried by colour alone,
          and this badge is read at a glance more than it is read. */}
      <span aria-hidden="true" className="size-1.5 rounded-full bg-current" />
      {kind}
    </Badge>
  );
}

export function NavLink({
  to,
  params,
  children,
}: {
  to: string;
  params?: object;
  children: ReactNode;
}) {
  const matchRoute = useMatchRoute();
  // `fuzzy` so a parent tab stays lit while a child route is open — the player
  // inspector is under Players, and Players should still look selected there.
  const active = Boolean(matchRoute({ to, params, fuzzy: true } as never));
  return (
    <Link
      to={to}
      params={params as never}
      aria-current={active ? "page" : undefined}
      className={cn(
        "rounded-md px-3 py-1.5 text-ui transition-colors",
        active
          ? "bg-surface-2 font-medium text-ink"
          : "text-ink-2 hover:bg-surface-2/60 hover:text-ink",
      )}
    >
      {children}
    </Link>
  );
}

/**
 * The screen for somebody who is not signed in.
 *
 * It is reached only when the automatic hand-off failed — see RootLayout. The
 * ordinary path never renders the card: a signed-out visitor goes straight to
 * control's sign-in screen, which is the one designed for the job and the one
 * all four clients share. Two screens saying "sign in" is one too many, and the
 * second one was a click in front of the first.
 *
 * So this is a fallback, and it says so. Its job is to be a way forward when
 * redirecting has already not worked, which is exactly when an automatic second
 * attempt would be a loop.
 */
export function SignedOut({
  retrying = false,
  stuck = false,
}: {
  retrying?: boolean;
  stuck?: boolean;
}) {
  const { signIn } = useSession();
  return (
    <DesignSignedOut
      // Until the hand-off has failed once, it is still in flight. Say nothing
      // louder than a line of text there: it is on screen for a few hundred
      // milliseconds and a spinner would flash.
      handingOff={!retrying}
      brand={
        <span className="flex items-center gap-2.5">
          <Logo className="size-[22px] text-ink" />
          <span className="text-ui font-semibold tracking-[-0.01em] text-ink">Magicweave</span>
        </span>
      }
      // "Not signed in" and not "that failed": this card is also what somebody
      // sees who signed out a moment ago and came straight back, and nothing
      // went wrong for them.
      status="Not signed in"
      product="Sign in to Magicweave"
      tagline={
        stuck
          ? "You were signed in again, and the console turned the session away straight after. This tab has stopped sending you to sign-in so it does not go round in circles."
          : "This tab was not taken to the sign-in screen automatically. Open it yourself and you will be back where you were."
      }
      onSignIn={() => void signIn()}
      onCreateAccount={() => void signIn({ create: true })}
      note={
        stuck
          ? "Signing in again may end up here too. If it does, wait a few minutes before trying again. Nothing will happen on its own."
          : "The hand-off to sign-in is taken once per tab and this tab did not get it. Nothing is loading and nothing will happen again on its own — the button is yours to press."
      }
    />
  );
}
